import { type ReactNode, useId, useState } from 'react';
import type {
  EligibilityAssertionPatch,
  EligibilityCurrentResponse,
  EligibilityHistoryEntry,
} from '@features/patient-coverages/types';
import { formatUsdCents } from '@features/patient-coverages/utils/patientCost';
import { ExpandMoreRounded, HistoryRounded } from '@mui/icons-material';
import { Alert, Box, Button, Collapse, Divider, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { ELIG_TOKENS } from '../theme/readinessTokens';
import { coverageContactTime, coverageSourceSummary } from '../utils/coveragePresentation';
import { FINANCIAL_FIELDS } from '../utils/verificationFinancials';

import { CoverageSourceDetails } from './CoverageSourceDetails';

type Field = keyof EligibilityAssertionPatch;
const fields: Field[] = ['coverage_result', ...FINANCIAL_FIELDS.map(field => field.apiKey)];
function label(field: Field) {
  return field === 'coverage_result'
    ? 'Coverage result'
    : FINANCIAL_FIELDS.find(item => item.apiKey === field)?.label;
}
function valueLabel(field: Field, value: EligibilityAssertionPatch[Field]) {
  if (value == null) return 'Unknown';
  if (typeof value === 'string') return value;
  return field === 'coinsurance_percent' ? `${value}%` : formatUsdCents(value);
}

export function CoverageRecordHistory({
  items,
  current,
  status,
}: {
  items: EligibilityHistoryEntry[];
  current: EligibilityCurrentResponse;
  status?: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const { eligibilityRecord: tracking } = useEventTracking();
  const summary = coverageSourceSummary(current);
  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 11.5, fontWeight: 720, color: ELIG_TOKENS.inkSoft }}>
            {current.state === 'empty' ? 'No source recorded yet' : summary.label}
          </Typography>
          {summary.source && (
            <Typography sx={{ fontSize: 11.5, color: ELIG_TOKENS.inkSoft }}>
              {summary.source.observed_at &&
              Number.isFinite(new Date(summary.source.observed_at).getTime())
                ? 'Checked '
                : ''}
              {coverageContactTime(summary.source.observed_at)}
            </Typography>
          )}
        </Box>
        <Button
          size="small"
          color="inherit"
          startIcon={<HistoryRounded />}
          endIcon={<ExpandMoreRounded sx={{ transform: open ? 'rotate(180deg)' : undefined }} />}
          aria-expanded={open}
          aria-controls={id}
          onClick={() => {
            if (open) tracking.sourceClosed();
            else tracking.sourceOpened();
            setOpen(value => !value);
          }}
          sx={{ flexShrink: 0 }}
        >
          {open ? 'Hide history' : 'View history'}
        </Button>
      </Stack>
      <Collapse in={open} id={id}>
        <Stack spacing={2} sx={{ pt: 2 }}>
          <Typography variant="h6">Source history</Typography>
          {status}
          {!status && !items.length && (
            <Typography color="text.secondary">No source history for this coverage.</Typography>
          )}
          {items.map(item => (
            <Box key={item.change_id ?? item.observation_id}>
              <Typography sx={{ fontWeight: 600 }}>
                {item.kind === 'manual'
                  ? 'Manual update'
                  : item.kind === 'automatic'
                    ? 'Automatic check'
                    : 'Earlier history'}
              </Typography>
              <Typography variant="caption">
                Recorded {new Date(item.recorded_at).toLocaleString()}
              </Typography>
              <CoverageSourceDetails fieldLabel="Original observation" source={item.provenance} />
              {fields
                .filter(field => Object.hasOwn(item.observed_values, field))
                .map(field => {
                  const difference = item.differences?.[field];
                  const currentValue =
                    field === 'coverage_result'
                      ? current.coverage_result
                      : current.benefits?.[field];
                  return difference ? (
                    <Alert severity="info" key={field} sx={{ my: 1 }}>
                      {label(field)}: automatic check reported{' '}
                      {valueLabel(field, item.observed_values[field])}. Current value remains{' '}
                      {valueLabel(field, currentValue)}.{' '}
                      {difference === 'human_owned'
                        ? 'Manually confirmed value preserved.'
                        : 'Earlier source is uncertain; no automatic replacement.'}
                    </Alert>
                  ) : (
                    <Typography variant="body2" key={field}>
                      {label(field)}: {valueLabel(field, item.observed_values[field])}
                    </Typography>
                  );
                })}
              <Divider sx={{ mt: 2 }} />
            </Box>
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}
